// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import { For, Show, createSignal, onCleanup, onMount } from "solid-js";
import {
  createInfiniteQuery,
  createQuery,
  useQueryClient,
} from "@tanstack/solid-query";
import Bell from "lucide-solid/icons/bell";
import Mail from "lucide-solid/icons/mail";
import MailOpen from "lucide-solid/icons/mail-open";
import X from "lucide-solid/icons/x";
import type { NotificationBody } from "./api/generated";
import {
  listNotifications,
  markNotificationRead,
  markNotificationUnread,
  notificationStatus,
} from "./api/generated";
import { authSlotHeaders } from "./api/runtime";
import { Heading } from "./components/layout";
import { formatDate } from "./format";
import { useGo } from "./navigation";

function NotificationReadToggle(p: {
  notification: NotificationBody;
  onChange: (notification: NotificationBody, read: boolean) => Promise<void>;
}) {
  const [busy, setBusy] = createSignal(false);
  const action = () => (p.notification.read ? "unread" : "read");
  return (
    <button
      type="button"
      class="btn btn-ghost btn-sm h-auto min-h-8 shrink-0 px-2 text-xs"
      aria-label={`Mark ${p.notification.name ?? p.notification.kind} as ${action()}`}
      disabled={busy()}
      onClick={async () => {
        setBusy(true);
        try {
          await p.onChange(p.notification, !p.notification.read);
        } finally {
          setBusy(false);
        }
      }}
    >
      {p.notification.read ? <Mail size={15} /> : <MailOpen size={15} />}
      <span>Mark as {action()}</span>
    </button>
  );
}
export function Notifications(p: { slot: number; account: string }) {
  const go = useGo();
  const [open, setOpen] = createSignal(false);
  let container!: HTMLDivElement;
  const queryClient = useQueryClient();
  const status = createQuery(() => ({
    queryKey: [p.slot, p.account, "notifications", "status"],
    queryFn: async () =>
      (
        await notificationStatus({
          throwOnError: true,
          responseStyle: "fields",
          path: { account: p.account },
          headers: authSlotHeaders(p.slot),
        })
      ).data,
    refetchInterval: () =>
      document.visibilityState === "visible" && navigator.onLine
        ? 10_000
        : false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  }));
  const notifications = createQuery(() => ({
    queryKey: [
      p.slot,
      p.account,
      "notifications",
      "recent",
      status.data?.latestId ?? "",
    ],
    queryFn: () =>
      listNotifications({
        throwOnError: true,
        responseStyle: "fields",
        path: { account: p.account },
        query: { limit: 10 },
        headers: authSlotHeaders(p.slot),
      }),
    enabled: open(),
    refetchInterval: () =>
      document.visibilityState === "visible" && navigator.onLine
        ? 10_000
        : false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  }));
  onMount(() => {
    const refetchVisible = () => {
      if (document.visibilityState === "visible" && navigator.onLine) {
        void status.refetch();
        if (open()) void notifications.refetch();
      }
    };
    const closeOutside = (event: PointerEvent) => {
      if (open() && !container.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("focus", refetchVisible);
    window.addEventListener("online", refetchVisible);
    document.addEventListener("visibilitychange", refetchVisible);
    document.addEventListener("pointerdown", closeOutside);
    onCleanup(() => {
      window.removeEventListener("focus", refetchVisible);
      window.removeEventListener("online", refetchVisible);
      document.removeEventListener("visibilitychange", refetchVisible);
      document.removeEventListener("pointerdown", closeOutside);
    });
  });
  const items = () => notifications.data?.data.items ?? [];
  const unread = () => status.data?.unread ?? 0;
  const changeRead = async (n: NotificationBody, read: boolean) => {
    const options = {
      throwOnError: true as const,
      responseStyle: "fields" as const,
      path: { id: n.id },
      headers: authSlotHeaders(p.slot),
    };
    if (read) await markNotificationRead(options);
    else await markNotificationUnread(options);
    await queryClient.invalidateQueries({
      queryKey: [p.slot, p.account, "notifications"],
    });
  };
  return (
    <div ref={container} class="relative">
      <button
        aria-label={`${unread()} unread notifications`}
        aria-haspopup="dialog"
        aria-expanded={open()}
        onClick={() => setOpen(!open())}
        class="header-action relative inline-flex"
      >
        <Bell size={21} />
        <Show when={unread()}>
          <span class="absolute -right-0.5 -top-0.5 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none text-white">
            {unread()}
          </span>
        </Show>
      </button>
      <Show when={open()}>
        <section
          aria-label="Notifications"
          class="popover fixed left-4 right-4 top-[4.5rem] z-[70] max-h-[min(30rem,calc(100vh-6rem))] overflow-y-auto p-2 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96"
        >
          <header class="flex items-center justify-between px-3 py-2">
            <h2 class="font-semibold text-slate-900">Notifications</h2>
            <button
              class="icon-action"
              aria-label="Close notifications"
              onClick={() => setOpen(false)}
            >
              <X size={18} />
            </button>
          </header>
          <Show
            when={items().length}
            fallback={
              <p class="px-3 py-6 text-sm text-slate-500">
                You’re all caught up.
              </p>
            }
          >
            <For each={items()}>
              {(n) => (
                <article
                  aria-label={n.name ?? n.kind}
                  class={`rounded-lg px-3 py-3 text-slate-800 ${n.read ? "" : "notification-unread"}`}
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                      <span
                        class={`block ${n.read ? "font-medium" : "font-bold"}`}
                      >
                        {n.name ?? n.kind}
                      </span>
                      <Show when={n.body}>
                        <span class="mt-0.5 block text-sm text-slate-600">
                          {n.body}
                        </span>
                      </Show>
                      <span class="mt-1 block text-xs text-slate-500">
                        {formatDate(n.createdAt)}
                      </span>
                    </div>
                    <NotificationReadToggle
                      notification={n}
                      onChange={changeRead}
                    />
                  </div>
                </article>
              )}
            </For>
          </Show>
          <button
            class="w-full rounded-lg px-3 py-2 text-sm font-medium text-primary hover:bg-slate-100"
            onClick={() => {
              setOpen(false);
              go(`/u${p.slot}/${p.account}/notifications`);
            }}
          >
            View notification history
          </button>
        </section>
      </Show>
    </div>
  );
}
function NotificationHistory(p: {
  items: NotificationBody[];
  onChange: (notification: NotificationBody, read: boolean) => Promise<void>;
}) {
  const [unreadOnly, setUnreadOnly] = createSignal(false);
  const items = () =>
    unreadOnly() ? p.items.filter((item) => !item.read) : p.items;
  return (
    <>
      <div role="group" aria-label="Notification filter" class="join mb-5">
        <button
          class={`btn btn-sm join-item ${unreadOnly() ? "btn-ghost" : "btn-primary"}`}
          aria-pressed={!unreadOnly()}
          onClick={() => setUnreadOnly(false)}
        >
          All
        </button>
        <button
          class={`btn btn-sm join-item ${unreadOnly() ? "btn-primary" : "btn-ghost"}`}
          aria-pressed={unreadOnly()}
          onClick={() => setUnreadOnly(true)}
        >
          Unread
        </button>
      </div>
      <Show
        when={items().length}
        fallback={
          <div class="panel p-8 text-center text-slate-500">
            No unread notifications.
          </div>
        }
      >
        <div class="grid gap-3">
          <For each={items()}>
            {(n) => (
              <article
                aria-label={n.name ?? n.kind}
                class={`panel flex flex-wrap items-start justify-between gap-4 p-5 ${n.read ? "" : "notification-unread"}`}
              >
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <h2 class="font-semibold">{n.name ?? n.kind}</h2>
                    <span class="text-xs text-slate-500">
                      {n.read ? "Read" : "Unread"}
                    </span>
                  </div>
                  <Show when={n.body}>
                    <p class="mt-1 text-sm text-slate-600">{n.body}</p>
                  </Show>
                  <p class="mt-2 text-xs text-slate-500">
                    {formatDate(n.createdAt)}
                  </p>
                </div>
                <NotificationReadToggle
                  notification={n}
                  onChange={p.onChange}
                />
              </article>
            )}
          </For>
        </div>
      </Show>
    </>
  );
}
export function NotificationsPage(p: { slot: number; account: string }) {
  const queryClient = useQueryClient();
  const notifications = createInfiniteQuery(() => ({
    queryKey: [p.slot, p.account, "notifications", "history"],
    queryFn: async ({ pageParam }) =>
      (
        await listNotifications({
          throwOnError: true,
          responseStyle: "fields",
          path: { account: p.account },
          query: { limit: 50, cursor: pageParam || undefined },
          headers: authSlotHeaders(p.slot),
        })
      ).data,
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    refetchInterval: () =>
      document.visibilityState === "visible" && navigator.onLine
        ? 10_000
        : false,
    refetchIntervalInBackground: false,
  }));
  const items = () =>
    notifications.data?.pages.flatMap((page) => page.items ?? []) ?? [];
  const changeRead = async (n: NotificationBody, read: boolean) => {
    const options = {
      throwOnError: true as const,
      responseStyle: "fields" as const,
      path: { id: n.id },
      headers: authSlotHeaders(p.slot),
    };
    if (read) await markNotificationRead(options);
    else await markNotificationUnread(options);
    await queryClient.invalidateQueries({
      queryKey: [p.slot, p.account, "notifications"],
    });
  };
  return (
    <>
      <Heading
        title="Notifications"
        copy="Review account activity and manage unread notifications."
      />
      <Show when={notifications.isPending}>
        <p>Loading notifications…</p>
      </Show>
      <Show when={notifications.isError}>
        <div role="alert" class="alert alert-error">
          Unable to load notifications. Please try again.
        </div>
      </Show>
      <Show when={notifications.isSuccess}>
        <NotificationHistory items={items()} onChange={changeRead} />
        <Show when={notifications.hasNextPage}>
          <div class="mt-5 flex justify-center">
            <button
              type="button"
              class="btn btn-outline"
              disabled={notifications.isFetchingNextPage}
              onClick={() => void notifications.fetchNextPage()}
            >
              {notifications.isFetchingNextPage
                ? "Loading more…"
                : "Load more notifications"}
            </button>
          </div>
        </Show>
      </Show>
    </>
  );
}
